import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSearchParams as useUrlSearchParams } from 'react-router-dom';
import SearchInput, { SearchInputHandle } from './SearchInput';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import i18next, { tEnum } from '../lang';
import { rootStore } from "../stores/RootStore";
import SortableTable, { SortableTableColumn } from './SortableTable';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewListIcon from '@mui/icons-material/ViewList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { adminStyles } from '../theme/adminStyles';
import MobileCardView, { MobileCardField } from './MobileCardView';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import InfiniteScrollFooter from './InfiniteScrollFooter';

export const getLevelColor = (level: string) => {
  switch (level) {
    case 'ADMIN':
      return 'error';
    case 'CORP_PARTNER':
      return 'primary';
    case 'BRAND_MANAGER':
      return 'info';
    case 'CLASSROOM_MANAGER':
      return 'secondary';
    default:
      return 'default';
  }
};

// 역할 Chip用の短いラベル
export const getLevelShortLabel = (level: string) => {
  switch (level) {
    case 'ADMIN':
      return 'Admin';
    case 'CORP_PARTNER':
      return i18next.t('enum.corp');
    case 'BRAND_MANAGER':
      return i18next.t('enum.brand');
    case 'CLASSROOM_MANAGER':
      return i18next.t('enum.class_head');
    case 'SUBCLASSROOM_MANAGER':
      return i18next.t('enum.sub_class_head');
    default:
      return level;
  }
};

export const levelChipSx = { width: 80, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.5 } };

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
      return 'primary';
    case 'NO_PERMISSION':
      return 'warning';
    default:
      return 'default';
  }
};

export interface User {
  id: string;
  username: string;
  email: string;
  phone: string;
  fax: string;
  level: string;
  organizationName: string;
  status: string;
  postage: number;
  companyName: string;
  companyUserId: string;
  brandName: string;
  brandUserId: string;
  classRoomName: string;
  dueDate: number | null;
  paymentMonth: number | null;
  paymentDate: number | null;
  invoiceUnit: string | null;
  allowedIps: string | null;
  isPrimaryAdmin: boolean | null;
  preferredLanguage: string | null;
  createdAt: string;
  updatedAt: string;
  additionalContacts: {
    id: number;
    userId: string;
    contactType: string;
    contactValue: string;
    createdAt: string;
    updatedAt: string;
  }[];
  addresses: {
    id: number;
    userId: string;
    postalCode: string;
    prefecture: string;
    city: string;
    streetAddress: string;
    building: string;
    aliasName: string;
    isDefault: number;
    createdAt: string;
    updatedAt: string;
  }[];
}

export interface TreeUser {
  id: string;
  username: string;
  email: string;
  phone: string;
  fax: string;
  level: string;
  organizationName: string;
  status: string;
  postage: number;
  invoiceUnit?: string | null;
  createdAt: string;
  children?: TreeUser[];
  childrenLoaded?: boolean;
  childrenLoading?: boolean;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString + 'Z');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const getHierarchy = (user: User) => {
  let hierarchy = '';
  if (user.companyName) {
    hierarchy += user.companyName;
    if (user.brandName) {
      hierarchy += ` > ${user.brandName}`;
      if (user.classRoomName) {
        hierarchy += ` > ${user.classRoomName}`;
      } else {
        hierarchy += ` > ${user.organizationName}`;
      }
    } else {
      hierarchy += ` > ${user.organizationName}`;
    }
  } else {
    hierarchy += user.organizationName;
  }

  return hierarchy;
};

// 固定幅コラム (border-box: paddingを含む)
const FIXED_WIDTHS: Record<string, number> = {
  id: 116, level: 116, phone: 136, fax: 136, status: 106, createdAt: 156, view: 106,
};

// 可変幅コラム制約 (min/max, border-box: paddingを含む)
const FLEX_CONSTRAINTS: Record<string, { min: number; max?: number }> = {
  username: { min: 176, max: 236 },
  organization: { min: 216 },
  email: { min: 196, max: 356 },
};

// 非表示優先順位: fax → email → organization → phone (作成日は常に表示)
const HIDE_ORDER = ['fax', 'email', 'organization', 'phone'];

// 組織名の親・祖父母を省略表示する関数
// CJK文字=1単位、ASCII文字=0.5単位として計算
const truncateAncestor = (name: string, maxUnits: number) => {
  let units = 0;
  for (let i = 0; i < name.length; i++) {
    const code = name.charCodeAt(i);
    // ASCII (Basic Latin) は0.5単位、それ以外は1単位
    units += code <= 0x7F ? 0.5 : 1;
    if (units > maxUnits) return name.slice(0, i) + '…';
  }
  return name;
};

export interface UserListViewRef {
  reload: () => void;
}

export interface UserListViewProps {
  // Actions
  actionLabel: string;                    // "상세 보기" or "구매 설정"
  onAction: (userId: string) => void;     // navigate callback

  // Status
  statusEditable?: boolean;               // default false - whether ADMIN can click status chip to change

  // Organization display
  showOrgHierarchy?: boolean;             // default true - show "company > brand > classroom" or just organizationName

  // Storage key prefix for localStorage persistence
  storageKeyPrefix?: string;              // default 'userMgmt' - for sort state persistence

  // Search filter - role filter shows only in list mode
  showRoleFilter?: boolean;               // default true

  // Extra action buttons to render in filter row
  filterActions?: React.ReactNode;

  // Initial status filter (e.g. 'PENDING')
  initialStatusFilter?: string;
}

const UserListView = forwardRef<UserListViewRef, UserListViewProps>(({
  actionLabel,
  onAction,
  statusEditable = false,
  showOrgHierarchy = true,
  storageKeyPrefix = 'userMgmt',
  showRoleFilter = true,
  filterActions,
  initialStatusFilter = '',
}, ref) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isNarrowOrg = useMediaQuery('(max-width:1980px)');
  const hideFax = useMediaQuery('(max-width:1800px)');
  const hideEmail = useMediaQuery('(max-width:1680px)');
  const hideEmailTree = useMediaQuery('(max-width:1600px)');
  const hidePhoneTree = useMediaQuery('(max-width:1024px)');
  const { authStore } = rootStore;

  // テーブルコンテナ幅測定 (callback refでモバイル→デスクトップ切替対応)
  const [containerWidth, setContainerWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const tableWrapperRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  // 溢れ防止のための自動カラム非表示 (メディアクエリベース)
  const hiddenColumns = useMemo(() => {
    const hidden = new Set<string>();

    // 1800px以下でFAXを非表示
    if (hideFax) hidden.add('fax');
    // 1680px以下でEmailを非表示
    if (hideEmail) hidden.add('email');

    // 残りのカラムはコンテナ溢れ時に順次非表示
    if (containerWidth > 0) {
      const flexMins = Object.fromEntries(
        Object.entries(FLEX_CONSTRAINTS).map(([k, v]) => [k, v.min])
      );
      const estimatedWidths: Record<string, number> = { ...FIXED_WIDTHS, ...flexMins };
      let totalWidth = Object.entries(estimatedWidths)
        .filter(([key]) => !hidden.has(key))
        .reduce((sum, [, w]) => sum + (w || 0), 0);

      for (const key of HIDE_ORDER) {
        if (hidden.has(key)) continue;
        if (totalWidth <= containerWidth) break;
        hidden.add(key);
        totalWidth -= estimatedWidths[key] || 0;
      }

      // 組織を非表示にする場合、電話番号も同時に非表示
      if (hidden.has('organization') && !hidden.has('phone')) {
        hidden.add('phone');
      }
    }
    return hidden;
  }, [containerWidth, hideFax, hideEmail]);

  // 可変幅コラムの実際の幅を計算 (min/max制約付き分配)
  const flexWidths = useMemo(() => {
    const minWidths = Object.fromEntries(
      Object.entries(FLEX_CONSTRAINTS).map(([k, v]) => [k, v.min])
    );
    if (containerWidth <= 0) return { ...minWidths };

    const visibleFixedTotal = Object.entries(FIXED_WIDTHS)
      .filter(([key]) => !hiddenColumns.has(key))
      .reduce((sum, [, w]) => sum + w, 0);

    const visibleFlexCols = Object.keys(FLEX_CONSTRAINTS)
      .filter(key => !hiddenColumns.has(key));

    if (visibleFlexCols.length === 0) return {};

    const remaining = containerWidth - visibleFixedTotal;
    const totalMin = visibleFlexCols.reduce((sum, key) => sum + FLEX_CONSTRAINTS[key].min, 0);

    if (remaining <= totalMin) {
      return Object.fromEntries(visibleFlexCols.map(key => [key, FLEX_CONSTRAINTS[key].min]));
    }

    // max制約付き分配: maxに達した列の余剰はorganization等に回す
    const result: Record<string, number> = {};
    let extraSpace = remaining - totalMin;
    const pending = [...visibleFlexCols];

    // まずmax制約のある列を処理
    for (const key of pending) {
      result[key] = FLEX_CONSTRAINTS[key].min;
    }

    let changed = true;
    while (changed && extraSpace > 0 && pending.length > 0) {
      changed = false;
      const perCol = extraSpace / pending.length;
      const capped: string[] = [];

      for (const key of pending) {
        const maxW = FLEX_CONSTRAINTS[key].max;
        const proposed = result[key] + perCol;
        if (maxW && proposed > maxW) {
          const added = maxW - result[key];
          result[key] = maxW;
          extraSpace -= added;
          capped.push(key);
          changed = true;
        }
      }

      // maxに達した列を除外して再分配
      for (const key of capped) {
        const idx = pending.indexOf(key);
        if (idx >= 0) pending.splice(idx, 1);
      }

      if (!changed && pending.length > 0) {
        // 残り全て均等分配
        const finalPer = extraSpace / pending.length;
        for (const key of pending) {
          result[key] = Math.floor(result[key] + finalPer);
        }
        extraSpace = 0;
      }
    }

    return result;
  }, [containerWidth, hiddenColumns]);

  // URL クエリパラメータ同期 (戻るボタンで検索結果状態を復元するため)
  const [urlParams, setUrlParams] = useUrlSearchParams();

  const [sortColumn, setSortColumn] = useState<string | null>(() => {
    const fromUrl = urlParams.get('sort');
    if (fromUrl !== null) return fromUrl === '' ? null : fromUrl;
    const saved = localStorage.getItem(`${storageKeyPrefix}_sortColumn`);
    return saved !== null ? (saved === '' ? null : saved) : 'createdAt';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const fromUrl = urlParams.get('dir');
    if (fromUrl === 'asc' || fromUrl === 'desc') return fromUrl;
    const saved = localStorage.getItem(`${storageKeyPrefix}_sortDirection`);
    return saved === 'asc' || saved === 'desc' ? saved : 'desc';
  });
  const [searchParams, setSearchParams] = useState({
    level: urlParams.get('level') || '',
    status: urlParams.get('status') ?? initialStatusFilter,
    keyword: urlParams.get('keyword') || '',
  });

  // ビューモード (URL > sessionStorage の優先順)
  const [viewMode, setViewMode] = useState<'list' | 'hierarchy'>(() => {
    const fromUrl = urlParams.get('view');
    if (fromUrl === 'hierarchy' || fromUrl === 'list') return fromUrl;
    const saved = sessionStorage.getItem(`${storageKeyPrefix}_viewMode`);
    return saved === 'hierarchy' ? 'hierarchy' : 'list';
  });

  // 現在の状態を URL クエリに同期する (replace: true で履歴を汚さない)
  const syncToUrl = useCallback((overrides?: {
    level?: string;
    status?: string;
    keyword?: string;
    viewMode?: 'list' | 'hierarchy';
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
  }) => {
    const next = new URLSearchParams();
    const level = overrides?.level !== undefined ? overrides.level : searchParams.level;
    const status = overrides?.status !== undefined ? overrides.status : searchParams.status;
    const keyword = overrides?.keyword !== undefined ? overrides.keyword : searchParams.keyword;
    const view = overrides?.viewMode !== undefined ? overrides.viewMode : viewMode;
    const sCol = overrides?.sortColumn !== undefined ? overrides.sortColumn : sortColumn;
    const sDir = overrides?.sortDirection !== undefined ? overrides.sortDirection : sortDirection;

    if (level) next.set('level', level);
    if (status) next.set('status', status);
    if (keyword) next.set('keyword', keyword);
    if (view && view !== 'list') next.set('view', view);
    if (sCol && sCol !== 'createdAt') next.set('sort', sCol);
    if (sDir && sDir !== 'desc') next.set('dir', sDir);

    setUrlParams(next, { replace: true });
  }, [searchParams.level, searchParams.status, searchParams.keyword, viewMode, sortColumn, sortDirection, setUrlParams]);

  const handleSetViewMode = (mode: 'list' | 'hierarchy') => {
    setViewMode(mode);
    sessionStorage.setItem(`${storageKeyPrefix}_viewMode`, mode);
    syncToUrl({ viewMode: mode });
    if (mode === 'list') {
      reload();
    } else {
      fetchCorpPartners();
    }
  };

  // 状態変更メニュー・ダイアログ
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusTargetUser, setStatusTargetUser] = useState<(User | TreeUser) | null>(null);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');

  // ===== リストビュー用 =====
  const fetchUsers = useCallback(async (page: number, pageSize: number) => {
    const params = new URLSearchParams();
    if (searchParams.level) params.append('level', searchParams.level);
    // showRoleFilterがfalseでリストモードの場合、CORP_PARTNERのみ表示
    if (!showRoleFilter && !searchParams.level) params.append('level', 'CORP_PARTNER');
    if (searchParams.status) params.append('status', searchParams.status);
    if (searchParams.keyword) params.append('keyword', searchParams.keyword);
    params.append('page', page.toString());
    params.append('rowsPerPage', pageSize.toString());
    if (sortColumn) {
      params.append('sortColumn', sortColumn);
      params.append('sortDirection', sortDirection);
    }

    const response = await axios.get('/api/v1/admin/users', { params });
    return { items: response.data.users, totalCount: response.data.totalCount };
  }, [searchParams.level, searchParams.status, searchParams.keyword, sortColumn, sortDirection, showRoleFilter]);

  const { items: users, totalCount, isLoading, hasMore, sentinelRef, reload } = useInfiniteScroll<User>({
    fetchFn: fetchUsers,
    pageSize: 20,
    resetDeps: [sortColumn, sortDirection, searchParams.level, searchParams.status, searchParams.keyword],
  });

  // ===== 階層ビュー用 =====
  const [corpUsers, setCorpUsers] = useState<TreeUser[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem(`${storageKeyPrefix}_expandedIds`);
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch { return new Set(); }
  });
  // expandedIds変更時にsessionStorageに保存
  const updateExpandedIds = (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    setExpandedIds(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      sessionStorage.setItem(`${storageKeyPrefix}_expandedIds`, JSON.stringify([...next]));
      return next;
    });
  };
  const restoreExpandedRef = useRef(true);
  const [treeLoading, setTreeLoading] = useState(false);

  // ログインユーザーのレベルに応じてツリーのルートレベルを決定
  const treeRootLevel = authStore.user?.level === 'CORP_PARTNER' ? 'BRAND_MANAGER'
    : authStore.user?.level === 'BRAND_MANAGER' ? 'CLASSROOM_MANAGER'
    : 'CORP_PARTNER';

  const fetchCorpPartners = useCallback(async () => {
    setTreeLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('level', treeRootLevel);
      if (searchParams.keyword) params.append('keyword', searchParams.keyword);
      // 状態フィルタはクライアント側でツリーフィルタリング
      params.append('page', '0');
      params.append('rowsPerPage', '100');

      const response = await axios.get('/api/v1/admin/users', { params });
      let treeUsers: TreeUser[] = (response.data.users || []).map((u: any) => ({
        id: u.id, username: u.username || '', email: u.email || '',
        phone: u.phone || '', fax: u.fax || '',
        level: u.level || '', organizationName: u.organizationName || '',
        status: u.status || '', postage: u.postage || 0, invoiceUnit: u.invoiceUnit || null, createdAt: u.createdAt || '',
        children: undefined, childrenLoaded: false, childrenLoading: false,
      }));
      // sessionStorageから展開状態を復元（初回マウント時のみ）
      if (restoreExpandedRef.current) {
        restoreExpandedRef.current = false;
        try {
          const saved = sessionStorage.getItem(`${storageKeyPrefix}_expandedIds`);
          if (saved) {
            const savedIds = new Set(JSON.parse(saved) as string[]);
            if (savedIds.size > 0) {
              let updated = treeUsers;
              let remaining = new Set(savedIds);
              let foundAny = true;
              while (remaining.size > 0 && foundAny) {
                foundAny = false;
                for (const id of [...remaining]) {
                  const node = findNode(updated, id);
                  if (node) {
                    if (!node.childrenLoaded) {
                      const children = await fetchChildren(id);
                      updated = updateNodeChildren(updated, id, children);
                    }
                    remaining.delete(id);
                    foundAny = true;
                  }
                }
              }
              setCorpUsers(updated);
              return;
            }
          }
        } catch { /* sessionStorage parse error - ignore */ }
      }
      setCorpUsers(treeUsers);
      updateExpandedIds(new Set());
    } catch (error) {
      console.error('Failed to fetch corp partners:', error);
    } finally {
      setTreeLoading(false);
    }
  }, [searchParams.keyword]);

  const fetchChildren = async (parentId: string): Promise<TreeUser[]> => {
    try {
      const response = await axios.get(`/api/v1/admin/users/${parentId}/childrens`);
      return (response.data || []).map((c: any) => ({
        id: c.id, username: c.username || '', email: c.email || '',
        phone: c.phone || '', fax: c.fax || '',
        level: c.level || '', organizationName: c.organizationName || '',
        status: c.status || '', postage: c.postage || 0, createdAt: c.createdAt || '',
        children: undefined, childrenLoaded: false, childrenLoading: false,
      }));
    } catch (error) {
      console.error(`Failed to fetch children for ${parentId}:`, error);
      return [];
    }
  };

  const updateNodeChildren = (nodes: TreeUser[], nodeId: string, children: TreeUser[]): TreeUser[] => {
    return nodes.map(u => {
      if (u.id === nodeId) return { ...u, children, childrenLoaded: true, childrenLoading: false };
      if (u.children) return { ...u, children: updateNodeChildren(u.children, nodeId, children) };
      return u;
    });
  };

  const setNodeLoading = (nodes: TreeUser[], nodeId: string, loading: boolean): TreeUser[] => {
    return nodes.map(u => {
      if (u.id === nodeId) return { ...u, childrenLoading: loading };
      if (u.children) return { ...u, children: setNodeLoading(u.children, nodeId, loading) };
      return u;
    });
  };

  const findNode = (nodes: TreeUser[], id: string): TreeUser | null => {
    for (const u of nodes) {
      if (u.id === id) return u;
      if (u.children) {
        const found = findNode(u.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const updateNodeInTree = (nodes: TreeUser[], nodeId: string, update: Partial<TreeUser>): TreeUser[] => {
    return nodes.map(u => {
      if (u.id === nodeId) return { ...u, ...update };
      if (u.children) return { ...u, children: updateNodeInTree(u.children, nodeId, update) };
      return u;
    });
  };

  const handleToggle = async (userId: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
      updateExpandedIds(newExpanded);
    } else {
      newExpanded.add(userId);
      updateExpandedIds(newExpanded);

      const node = findNode(corpUsers, userId);
      if (node && !node.childrenLoaded) {
        setCorpUsers(prev => setNodeLoading(prev, userId, true));
        const children = await fetchChildren(userId);
        setCorpUsers(prev => updateNodeChildren(prev, userId, children));
      }
    }
  };

  const expandingRef = useRef(false);
  const handleExpandAll = async () => {
    if (expandingRef.current) return;
    expandingRef.current = true;
    setTreeLoading(true);
    try {
      const newUsers = [...corpUsers];
      const allIds = new Set<string>();

      // レベル1: 法人→ブランド
      for (let i = 0; i < newUsers.length; i++) {
        if (!newUsers[i].childrenLoaded) {
          const children = await fetchChildren(newUsers[i].id);
          newUsers[i] = { ...newUsers[i], children, childrenLoaded: true, childrenLoading: false };
        }
        allIds.add(newUsers[i].id);

        // レベル2: ブランド→教場
        if (newUsers[i].children) {
          for (let j = 0; j < newUsers[i].children!.length; j++) {
            const child = newUsers[i].children![j];
            if (child.level === 'BRAND_MANAGER' && !child.childrenLoaded) {
              const grandChildren = await fetchChildren(child.id);
              newUsers[i].children![j] = { ...child, children: grandChildren, childrenLoaded: true, childrenLoading: false };
            }
            if (child.level !== 'CLASSROOM_MANAGER') {
              allIds.add(child.id);
            }
          }
        }
      }

      setCorpUsers(newUsers);
      updateExpandedIds(allIds);
    } finally {
      setTreeLoading(false);
      expandingRef.current = false;
    }
  };

  const handleCollapseAll = () => {
    updateExpandedIds(new Set());
  };

  // 階層ビュー: 初回ロード
  const treeLoadedRef = useRef(false);
  useEffect(() => {
    if (viewMode === 'hierarchy' && !treeLoadedRef.current) {
      treeLoadedRef.current = true;
      fetchCorpPartners();
    }
  }, [viewMode]);

  // 階層ビュー: 状態フィルタ変更 → 自動展開
  const prevStatusRef = useRef(searchParams.status);
  useEffect(() => {
    if (viewMode === 'hierarchy' && searchParams.status && searchParams.status !== prevStatusRef.current && corpUsers.length > 0) {
      handleExpandAll();
    }
    prevStatusRef.current = searchParams.status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.status]);

  // 階層ビュー: ステータスフィルタリング (ツリー表示用)
  const shouldShowNode = useCallback((node: TreeUser, statusFilter: string): boolean => {
    if (!statusFilter) return true;
    if (node.status === statusFilter) return true;
    if (node.children) {
      return node.children.some(child => shouldShowNode(child, statusFilter));
    }
    return false;
  }, []);

  const canExpand = (level: string) => level === 'CORP_PARTNER' || level === 'BRAND_MANAGER';

  // ===== 共通ハンドラー =====
  const handleSort = (columnKey: string) => {
    let newCol: string | null;
    let newDir: 'asc' | 'desc';
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        newCol = columnKey;
        newDir = 'desc';
      } else {
        // desc → 정렬 해제
        newCol = null;
        newDir = 'asc';
      }
    } else {
      newCol = columnKey;
      newDir = 'asc';
    }
    setSortColumn(newCol);
    setSortDirection(newDir);
    localStorage.setItem(`${storageKeyPrefix}_sortColumn`, newCol ?? '');
    localStorage.setItem(`${storageKeyPrefix}_sortDirection`, newDir);
    syncToUrl({ sortColumn: newCol, sortDirection: newDir });
  };

  const searchInputRef = useRef<SearchInputHandle>(null);

  const handleSearch = (keyword?: string) => {
    const k = keyword !== undefined ? keyword : (searchInputRef.current?.getValue() ?? searchParams.keyword);
    setSearchParams({ ...searchParams, keyword: k });
    syncToUrl({ keyword: k });
    // searchParams.keyword は useInfiniteScroll の resetDeps に含まれているため、
    // state 更新後の re-render で自動的に reload される。階層モードは別途呼び出し。
    if (viewMode === 'hierarchy') {
      // hierarchy モードは fetchCorpPartners も searchParams.keyword に依存するため、
      // 次の re-render を待ってから呼ぶ必要があるが、useEffect で監視する方がクリーン
      fetchCorpPartners();
    }
  };

  const handleActionClick = (userId: string) => {
    onAction(userId);
  };

  const getRoleOptions = () => {
    const options = [];

    if (authStore.user?.level === 'ADMIN') {
      options.push(
        { value: 'ADMIN', label: i18next.t('user.admin') },
        { value: 'CORP_PARTNER', label: i18next.t('user.corp_admin') },
        { value: 'BRAND_MANAGER', label: i18next.t('user.brand_admin') },
        { value: 'CLASSROOM_MANAGER', label: i18next.t('user.classroom_admin') }
      );
    } else if (authStore.user?.level === 'CORP_PARTNER') {
      options.push(
        { value: 'BRAND_MANAGER', label: i18next.t('user.brand_admin') },
        { value: 'CLASSROOM_MANAGER', label: i18next.t('user.classroom_admin') }
      );
    } else if (authStore.user?.level === 'BRAND_MANAGER') {
      options.push(
        { value: 'CLASSROOM_MANAGER', label: i18next.t('user.classroom_admin') }
      );
    }

    return options;
  };

  const handleStatusChipClick = (event: React.MouseEvent<HTMLElement>, user: User | TreeUser) => {
    event.stopPropagation();
    setStatusTargetUser(user);
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  const handleStatusMenuSelect = (newStatus: string) => {
    setPendingStatus(newStatus);
    setStatusMenuAnchor(null);
    // Menu閉鎖とDialog表示を別レンダーサイクルに分離（MUI ModalManager競合回避）
    setTimeout(() => setStatusConfirmOpen(true), 0);
  };

  const handleStatusConfirm = async () => {
    if (!statusTargetUser) return;
    try {
      let putData: any;
      if (viewMode === 'hierarchy') {
        // TreeUserはフィールド不足のため、完全なデータを取得してステータスのみ変更
        const { data: fullUser } = await axios.get(`/api/v1/admin/users/${statusTargetUser.id}`);
        const { addresses, ...userFields } = fullUser;
        putData = { ...userFields, status: pendingStatus };
      } else {
        const { children, childrenLoaded, childrenLoading, ...userData } = statusTargetUser as any;
        putData = { ...userData, status: pendingStatus };
      }
      await axios.put(`/api/v1/admin/users/${statusTargetUser.id}`, putData);
      if (viewMode === 'list') {
        reload();
      } else {
        setCorpUsers(prev => updateNodeInTree(prev, statusTargetUser.id, { status: pendingStatus }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
    setStatusConfirmOpen(false);
    setStatusTargetUser(null);
    setPendingStatus('');
  };

  const handleStatusConfirmClose = () => {
    setStatusConfirmOpen(false);
    setStatusTargetUser(null);
    setPendingStatus('');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED': return i18next.t('common.approve');
      case 'PENDING': return i18next.t('common.pending_approval');
      case 'DELETED': return i18next.t('common.delete');
      default: return status;
    }
  };

  // refでreloadを公開
  useImperativeHandle(ref, () => ({
    reload: () => {
      if (viewMode === 'list') {
        reload();
      } else {
        fetchCorpPartners();
      }
    },
  }), [viewMode, reload, fetchCorpPartners]);

  // ===== ステータスChipレンダリング =====
  const renderStatusChip = (user: User | TreeUser, chipProps?: object) => {
    if (statusEditable && authStore.user?.level === 'ADMIN') {
      return (
        <Chip
          label={tEnum(user.status)}
          color={getStatusColor(user.status)}
          size="small"
          deleteIcon={<EditIcon sx={{ fontSize: '0.9rem !important' }} />}
          onDelete={(e) => handleStatusChipClick(e, user)}
          onClick={(e) => handleStatusChipClick(e, user)}
          sx={{ cursor: 'pointer' }}
          {...chipProps}
        />
      );
    }
    return (
      <Chip
        label={tEnum(user.status)}
        color={getStatusColor(user.status)}
        size="small"
        {...chipProps}
      />
    );
  };

  // ===== リストビュー: カラム定義 =====
  const columns: SortableTableColumn<User>[] = useMemo(() => [
    {
      key: 'id',
      label: i18next.t('common.id'),
      width: FIXED_WIDTHS.id,
      align: 'left' as const,
    },
    {
      key: 'username',
      label: i18next.t('user.username'),
      width: flexWidths.username,
      align: 'left' as const,
    },
    {
      key: 'level',
      label: i18next.t('common.role'),
      width: FIXED_WIDTHS.level,
      renderCell: (row: User) => (
        <Chip
          label={getLevelShortLabel(row.level)}
          color={getLevelColor(row.level)}
          size="small"
          sx={levelChipSx}
        />
      ),
    },
    {
      key: 'organization',
      label: i18next.t('common.organization'),
      width: flexWidths.organization,
      align: 'left' as const,
      hidden: hiddenColumns.has('organization'),
      renderCell: (row: User) => {
        if (!showOrgHierarchy) {
          return row.organizationName;
        }
        const hierarchy = getHierarchy(row);
        const parts = hierarchy.split(' > ');
        const ancestors = parts.slice(0, -1);
        const self = parts[parts.length - 1];
        const displayAncestors = isNarrowOrg
          ? ancestors.map(a => truncateAncestor(a, 4))
          : ancestors;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {displayAncestors.length > 0 && (
              <>{displayAncestors.join(' > ')} &gt;&nbsp;</>
            )}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {self}
            </Box>
          </Box>
        );
      },
    },
    {
      key: 'email',
      label: i18next.t('contact.email_label'),
      width: flexWidths.email,
      align: 'left' as const,
      hidden: hiddenColumns.has('email'),
    },
    {
      key: 'phone',
      label: i18next.t('contact.phone'),
      width: FIXED_WIDTHS.phone,
      align: 'left' as const,
      hidden: hiddenColumns.has('phone'),
    },
    {
      key: 'fax',
      label: i18next.t('contact.fax'),
      width: FIXED_WIDTHS.fax,
      align: 'left' as const,
      hidden: hiddenColumns.has('fax'),
    },
    {
      key: 'status',
      label: i18next.t('common.status'),
      width: FIXED_WIDTHS.status,
      renderCell: (row: User) => renderStatusChip(row),
    },
    {
      key: 'createdAt',
      label: i18next.t('common.created_at'),
      width: FIXED_WIDTHS.createdAt,
      hidden: hiddenColumns.has('createdAt'),
      renderCell: (row: User) => formatDate(row.createdAt),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [hiddenColumns, isNarrowOrg, flexWidths, statusEditable, actionLabel, showOrgHierarchy, i18next.language]);

  const mobileCardFields: MobileCardField<User>[] = [
    { key: 'username', label: i18next.t('user.username'), primary: true },
    {
      key: 'level', label: i18next.t('common.role'),
      render: (row) => <Chip label={getLevelShortLabel(row.level)} color={getLevelColor(row.level)} size="small" sx={levelChipSx} />
    },
    {
      key: 'organizationName', label: i18next.t('common.organization'),
      getValue: (row) => showOrgHierarchy ? getHierarchy(row) : row.organizationName,
    },
    { key: 'email', label: i18next.t('contact.email_label') },
    { key: 'phone', label: i18next.t('contact.phone'), getValue: (row) => row.phone || '-' },
    { key: 'fax', label: 'FAX', getValue: (row) => row.fax || '-' },
    {
      key: 'status', label: i18next.t('common.status'),
      render: (row) => {
        if (statusEditable && authStore.user?.level === 'ADMIN') {
          return (
            <Chip label={tEnum(row.status)} color={getStatusColor(row.status)} size="small"
              icon={<EditIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={(e) => { e.stopPropagation(); handleStatusChipClick(e, row); }} sx={{ cursor: 'pointer' }} />
          );
        }
        return (
          <Chip label={tEnum(row.status)} color={getStatusColor(row.status)} size="small" />
        );
      }
    },
    {
      key: 'action', label: '', hideLabel: true,
      render: (row) => (
        <Button size="small" variant="outlined" fullWidth onClick={(e) => { e.stopPropagation(); handleActionClick(row.id); }}>
          {actionLabel}
        </Button>
      )
    }
  ];

  // ===== 階層ビュー: 行レンダリング =====
  const renderTreeRow = (user: TreeUser, depth: number = 0) => {
    const isExpanded = expandedIds.has(user.id);
    const expandable = canExpand(user.level);
    const indentPx = depth * 20;

    return (
      <React.Fragment key={user.id}>
        <TableRow
          hover
          sx={{
            cursor: 'pointer',
            bgcolor: depth === 0 ? 'transparent' : depth === 1 ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.04)',
            '&:hover': {
              bgcolor: depth === 0 ? 'rgba(0,0,0,0.04)' : depth === 1 ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.08)',
            },
          }}
          onClick={() => handleActionClick(user.id)}
        >
          {/* ID + 펼치기/접기 */}
          <TableCell sx={{ pl: `${16 + indentPx}px`, whiteSpace: 'nowrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {expandable ? (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleToggle(user.id); }}
                  sx={{ mr: 0.5, p: '2px' }}
                >
                  {user.childrenLoading ? (
                    <CircularProgress size={18} />
                  ) : isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 28, mr: 0.5 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 'bold' : 'normal' }}>
                {user.id}
              </Typography>
            </Box>
          </TableCell>

          {/* 조직명 */}
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="body2">{user.organizationName || '-'}</Typography>
          </TableCell>

          {/* 역할 */}
          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
            <Chip
              label={getLevelShortLabel(user.level)}
              color={getLevelColor(user.level)}
              size="small"
              sx={levelChipSx}
            />
          </TableCell>

          {/* Email */}
          {!isSmallScreen && !hideEmailTree && (
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2">{user.email}</Typography>
            </TableCell>
          )}

          {/* 전화번호 */}
          {!hidePhoneTree && (
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2">{user.phone || '-'}</Typography>
            </TableCell>
          )}

          {/* FAX */}
          {!isSmallScreen && !hideFax && (
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2">{user.fax || '-'}</Typography>
            </TableCell>
          )}

          {/* 상태 */}
          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
            {user.status && renderStatusChip(user)}
          </TableCell>

          {/* 작성일 */}
          {!isSmallScreen && (
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2">{user.createdAt ? formatDate(user.createdAt) : '-'}</Typography>
            </TableCell>
          )}

          {/* Actions */}
          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
            <Button
              size="small"
              startIcon={<SearchIcon sx={{ m: 0, p: 0 }} />}
              onClick={(e) => { e.stopPropagation(); handleActionClick(user.id); }}
              sx={{
                fontSize: '0.7rem',
                minWidth: 'unset',
                whiteSpace: 'nowrap',
                p: 0,
                m: 0,
                '& .MuiButton-startIcon': {
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                },
              }}
            >
              {actionLabel}
            </Button>
          </TableCell>
        </TableRow>

        {/* 자식 행들 */}
        {expandable && isExpanded && user.children?.filter(c => shouldShowNode(c, searchParams.status)).map(child =>
          renderTreeRow(child, depth + 1)
        )}
      </React.Fragment>
    );
  };

  // モバイルカード (階層) - 連結線が子孫ツリー全体を貫通する構造
  const renderTreeMobileCard = (user: TreeUser, depth: number = 0, isLastSibling: boolean = true) => {
    const isExpanded = expandedIds.has(user.id);
    const expandable = canExpand(user.level);
    const connectorColor = '#bbb';
    const filteredChildren = expandable && isExpanded
      ? (user.children?.filter(c => shouldShowNode(c, searchParams.status)) || [])
      : [];

    // depth === 0: コネクタなし、直接レンダリング
    if (depth === 0) {
      return (
        <Box key={user.id}>
          <Paper
            elevation={0}
            sx={{
              mb: 1, p: 1.5,
              border: '1px solid', borderColor: 'primary.light', borderRadius: '8px',
              cursor: 'pointer', backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
            onClick={() => handleActionClick(user.id)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              {expandable && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggle(user.id); }} sx={{ mr: 0.5, p: '2px' }}>
                  {user.childrenLoading ? <CircularProgress size={16} /> : isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              )}
              <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1 }}>{user.organizationName || user.id}</Typography>
              <Chip label={getLevelShortLabel(user.level)} color={getLevelColor(user.level)} size="small" sx={levelChipSx} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{user.id}</Typography>
              {user.status && renderStatusChip(user, { sx: { height: '20px', fontSize: '0.7rem', ...(statusEditable && authStore.user?.level === 'ADMIN' ? { cursor: 'pointer' } : {}) } })}
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', '& > div': { display: 'flex', justifyContent: 'space-between', py: '2px', borderBottom: '1px solid', borderColor: 'divider' } }}>
              <Box><span>Email</span><span>{user.email || '-'}</span></Box>
              <Box><span>{i18next.t('contact.phone')}</span><span>{user.phone || '-'}</span></Box>
              <Box><span>FAX</span><span>{user.fax || '-'}</span></Box>
              <Box><span>{i18next.t('common.created_at')}</span><span>{user.createdAt ? formatDate(user.createdAt) : '-'}</span></Box>
            </Box>
            <Box sx={{ mt: 0.5 }}>
              <Button size="small" variant="outlined" fullWidth onClick={(e) => { e.stopPropagation(); handleActionClick(user.id); }}>
                {actionLabel}
              </Button>
            </Box>
          </Paper>
          {/* 子ノード */}
          {filteredChildren.map((child, idx) =>
            renderTreeMobileCard(child, depth + 1, idx === filteredChildren.length - 1)
          )}
        </Box>
      );
    }

    // depth > 0: コネクタ列 + コンテンツ列の構造
    // コネクタ列は子孫を含む全高さに伸びる
    return (
      <Box key={user.id} sx={{ display: 'flex' }}>
        {/* コネクタ列: 縦線 + 横線 */}
        <Box sx={{
          width: 24, flexShrink: 0, position: 'relative',
          // 縦線: 最後の兄弟ならカードの中央まで、それ以外は全高
          '&::before': {
            content: '""', position: 'absolute',
            left: '12px', top: 0,
            height: isLastSibling ? '24px' : '100%',
            borderLeft: `2px solid ${connectorColor}`,
          },
          // 横線: カード上部から右へ
          '&::after': {
            content: '""', position: 'absolute',
            left: '12px', top: '24px',
            width: '12px',
            borderTop: `2px solid ${connectorColor}`,
          },
        }} />
        {/* コンテンツ列: カード + 子ノード（コネクタの高さが自然に拡張） */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              mb: 1, p: 1.5, mt: '4px',
              border: '1px solid',
              borderColor: depth === 1 ? 'info.light' : 'divider',
              borderRadius: '8px', cursor: 'pointer',
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
            onClick={() => handleActionClick(user.id)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              {expandable && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggle(user.id); }} sx={{ mr: 0.5, p: '2px' }}>
                  {user.childrenLoading ? <CircularProgress size={16} /> : isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              )}
              <Typography variant="body1" sx={{ fontWeight: 'normal', flex: 1 }}>{user.organizationName || user.id}</Typography>
              <Chip label={getLevelShortLabel(user.level)} color={getLevelColor(user.level)} size="small" sx={levelChipSx} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{user.id}</Typography>
              {user.status && renderStatusChip(user, { sx: { height: '20px', fontSize: '0.7rem', ...(statusEditable && authStore.user?.level === 'ADMIN' ? { cursor: 'pointer' } : {}) } })}
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', '& > div': { display: 'flex', justifyContent: 'space-between', py: '2px', borderBottom: '1px solid', borderColor: 'divider' } }}>
              <Box><span>Email</span><span>{user.email || '-'}</span></Box>
              <Box><span>{i18next.t('contact.phone')}</span><span>{user.phone || '-'}</span></Box>
              <Box><span>FAX</span><span>{user.fax || '-'}</span></Box>
              <Box><span>{i18next.t('common.created_at')}</span><span>{user.createdAt ? formatDate(user.createdAt) : '-'}</span></Box>
            </Box>
            <Box sx={{ mt: 0.5 }}>
              <Button size="small" variant="outlined" fullWidth onClick={(e) => { e.stopPropagation(); handleActionClick(user.id); }}>
                {actionLabel}
              </Button>
            </Box>
          </Paper>
          {/* 子ノード（コンテンツ列内なのでコネクタ列が自然に延びる） */}
          {filteredChildren.map((child, idx) =>
            renderTreeMobileCard(child, depth + 1, idx === filteredChildren.length - 1)
          )}
        </Box>
      </Box>
    );
  };

  const filteredCorpUsers = corpUsers.filter(u => shouldShowNode(u, searchParams.status));

  return (
    <>
      {/* 検索・フィルター */}
      <Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.filterPaper as any, ...(isMobile && { p: 1.5, mb: 1.5, borderRadius: 0, boxShadow: 'none', border: 'none' }) }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: isMobile ? 0.5 : 2 }}>
          {/* ビューモード切替 */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && handleSetViewMode(v)}
            size="small"
            sx={{ flexShrink: 0, '& .MuiToggleButton-root': { height: 31, width: 31, p: 0 } }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ fontSize: '1.1rem' }} />
            </ToggleButton>
            <ToggleButton value="hierarchy" aria-label="hierarchy view">
              <AccountTreeIcon sx={{ fontSize: '1.1rem' }} />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* 역할 필터 (리스트만, showRoleFilter가 true일 때) */}
          {viewMode === 'list' && showRoleFilter && (
            <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 100, flexShrink: 0, '& .MuiOutlinedInput-root': { height: 31, fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem', transform: 'translate(14px, 6px) scale(1)', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' } } }}>
              <InputLabel>{i18next.t('common.role')}</InputLabel>
              <Select
                value={searchParams.level}
                label={i18next.t('common.role')}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchParams({ ...searchParams, level: v });
                  syncToUrl({ level: v });
                }}
              >
                <MenuItem value="">{i18next.t('common.all')}</MenuItem>
                {getRoleOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* 상태 필터 */}
          <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 100, flexShrink: 0, '& .MuiOutlinedInput-root': { height: 31, fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem', transform: 'translate(14px, 6px) scale(1)', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' } } }}>
            <InputLabel>{i18next.t('common.status')}</InputLabel>
            <Select
              value={searchParams.status}
              label={i18next.t('common.status')}
              onChange={(e) => {
                const v = e.target.value;
                setSearchParams({ ...searchParams, status: v });
                syncToUrl({ status: v });
              }}
            >
              <MenuItem value="">{i18next.t('common.all')}</MenuItem>
              <MenuItem value="APPROVED">{i18next.t('common.approval_complete')}</MenuItem>
              <MenuItem value="PENDING">{i18next.t('common.pending_approval')}</MenuItem>
            </Select>
          </FormControl>

          {/* 키워드 검색 */}
          <SearchInput
            ref={searchInputRef}
            initialValue={searchParams.keyword}
            onCommit={handleSearch}
            variant="outlined"
            size="small"
            placeholder={isMobile ? i18next.t('common.search') : i18next.t('user.search_hint')}
            sx={{ flex: 1, minWidth: isMobile ? 60 : 100, '& .MuiOutlinedInput-root': { height: 31, fontSize: '0.8125rem' } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => handleSearch()}
            sx={{ ...adminStyles.searchButton, flexShrink: 0, ...(isMobile && { minWidth: 31, px: 0.5 }) }}
          >
            {isMobile ? <SearchIcon sx={{ fontSize: '1.1rem' }} /> : i18next.t('common.search')}
          </Button>

          {/* 모두 펼치기/접기 (계층만) */}
          {viewMode === 'hierarchy' && (
            <>
              <Tooltip title={i18next.t('product_mapping.expand_all')}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExpandAll}
                  disabled={treeLoading}
                  sx={{ flexShrink: 0, minWidth: 'unset', px: 0.5 }}
                >
                  <UnfoldMoreIcon fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title={i18next.t('product_mapping.collapse_all')}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCollapseAll}
                  sx={{ flexShrink: 0, minWidth: 'unset', px: 0.5 }}
                >
                  <UnfoldLessIcon fontSize="small" />
                </Button>
              </Tooltip>
            </>
          )}

          {/* 추가 액션 버튼 (태블릿+데스크톱: 인라인) */}
          {!isPhone && filterActions && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }}>
              {filterActions}
            </Stack>
          )}
        </Stack>
        {/* 추가 액션 버튼 (폰: 2번째 줄) */}
        {isPhone && filterActions && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            {filterActions}
          </Stack>
        )}
      </Paper>

      {/* コンテンツ */}
      {viewMode === 'list' ? (
        /* リストビュー */
        <Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.tablePaper as any, ...(isMobile && { borderRadius: 0, boxShadow: 'none', border: 'none', overflow: 'visible' }) }}>
          {isLoading && users.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <MobileCardView
              data={users}
              fields={mobileCardFields}
              onRowClick={(row) => handleActionClick(row.id)}
              keyExtractor={(row) => row.id}
              cardSx={() => ({})}
            />
          ) : (
            <Box ref={tableWrapperRef} sx={{ overflow: 'hidden' }}>
              <SortableTable
                columns={columns}
                rows={users}
                rowKey={(row) => row.id}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                onRowClick={(row) => handleActionClick(row.id)}
                fixedLayout
                rowSx={() => ({})}
              />
            </Box>
          )}
          <InfiniteScrollFooter
            loadedCount={users.length}
            totalCount={totalCount}
            isLoading={isLoading}
            hasMore={hasMore}
            sentinelRef={sentinelRef}
          />
        </Paper>
      ) : (
        /* 階層ビュー */
        <Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.tablePaper as any, ...(isMobile && { borderRadius: 0, boxShadow: 'none', border: 'none', overflow: 'visible' }) }}>
          {treeLoading && corpUsers.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 1 }}>
              {filteredCorpUsers.map((user, idx) => renderTreeMobileCard(user, 0, idx === filteredCorpUsers.length - 1))}
              {filteredCorpUsers.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">{i18next.t('common.no_search_results')}</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: 150 }}>{i18next.t('common.id')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('common.organization')}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: 120 }}>{i18next.t('common.role')}</TableCell>
                    {!isSmallScreen && !hideEmailTree && (
                      <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('contact.email_label')}</TableCell>
                    )}
                    {!hidePhoneTree && (
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{i18next.t('contact.phone')}</TableCell>
                    )}
                    {!isSmallScreen && !hideFax && (
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{i18next.t('contact.fax')}</TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>{i18next.t('common.status')}</TableCell>
                    {!isSmallScreen && (
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{i18next.t('common.created_at')}</TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCorpUsers.map(user => renderTreeRow(user))}
                  {filteredCorpUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isSmallScreen ? 5 : 9 - (hideFax ? 1 : 0) - (hideEmailTree ? 1 : 0)} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{i18next.t('common.no_search_results')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* 状態変更メニュー (statusEditable時のみ表示) */}
      {statusEditable && (
        <>
          <Menu
            anchorEl={statusMenuAnchor}
            open={Boolean(statusMenuAnchor)}
            onClose={handleStatusMenuClose}
          >
            {statusTargetUser?.status !== 'APPROVED' && (
              <MenuItem onClick={() => handleStatusMenuSelect('APPROVED')}>
                {i18next.t('common.approve')}
              </MenuItem>
            )}
            {statusTargetUser?.status !== 'PENDING' && (
              <MenuItem onClick={() => handleStatusMenuSelect('PENDING')}>
                {i18next.t('common.pending_approval')}
              </MenuItem>
            )}
            {statusTargetUser?.status !== 'DELETED' && (
              <MenuItem onClick={() => handleStatusMenuSelect('DELETED')}>
                {i18next.t('common.delete')}
              </MenuItem>
            )}
          </Menu>

          {/* 状態変更確認ダイアログ */}
          <Dialog open={statusConfirmOpen} onClose={handleStatusConfirmClose} maxWidth="xs" fullWidth>
            <DialogTitle>{i18next.t('user.change_status')}</DialogTitle>
            <DialogContent>
              <Typography>
                {i18next.t('user.confirm_status_change', {
                  username: `${statusTargetUser?.username || statusTargetUser?.id} (${statusTargetUser?.id})`,
                  status: getStatusLabel(pendingStatus),
                })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleStatusConfirmClose}>{i18next.t('common.cancel')}</Button>
              <Button onClick={handleStatusConfirm} variant="contained" color="primary">
                {i18next.t('common.change')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
});

UserListView.displayName = 'UserListView';

export default UserListView;
